import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../models/NameUtils';
import { useCoachingRecapMutation } from '../../../queries/PhoneCall/useCoachingRecapMutation';
import { type PhoneCallView, usePhoneCallFilterQuery } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { InViewInterceptor } from '../../../web/components/InViewInterceptor';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { accessibilityStyles } from '../../../web/styles/styles';
import { apiErrorMessage } from '../../focused-view/data/apiErrorMessage';
import { formatAgentTemplate, formatCallDuration, formatCallTimestamp } from './callDisplay';
import { FreshnessBadge } from './FreshnessBadge';
import { getCallSubject } from './leadLabel';
import { RepCardFilterBar } from './RepCardFilterBar';
import {
	applyClientCallFilters,
	buildRepCallCriteria,
	formatAdherencePercent,
	hasActiveRepCardFilters,
	hasClientCallFilters,
	scorePillColor,
} from './repCardFilters';
import { styleSheet } from './repCards.styles';
import { RUBRIC_SCORE_CONTROLS_ENABLED } from './rubricScoreControls';
import { useHighScoreCallAlerts } from './useHighScoreCallAlerts';
import { usePersistedRepCardFilters, usePersistedRepCardFiltersOpen } from './usePersistedRepCardFilters';
import type { ScoreAlertSettings } from './useScoreAlertSettings';

/** Slow enough for a grid of cards, fast enough that a finished call surfaces while it still matters. */
const POLL_INTERVAL_MS = 120_000;

const RefreshIcon = () => (
	<svg
		width='14'
		height='14'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M23 4v6h-6' />
		<path d='M20.49 15a9 9 0 1 1-2.12-9.36L23 10' />
	</svg>
);

const RecapIcon = () => (
	<svg
		width='14'
		height='14'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' />
		<path d='m22 6-10 7L2 6' />
	</svg>
);

interface IAdherencePillProps {
	name: string;
	detail: string;
	score?: number | null;
	value: string;
}

const AdherencePill = ({ name, detail, score, value }: IAdherencePillProps) => (
	<div title={`${name} — ${detail}`}>
		<span className={css(accessibilityStyles.srOnly)}>{name}</span>
		<Pill variant='tag' size='small' color={scorePillColor(score)} label={value} />
	</div>
);

interface IProps {
	repUser: Api.IUser;
	selectedConversationId: string | null;
	alertSettings: ScoreAlertSettings;
	onSelectCall: (aiConversationId: string) => void;
	onRemove?: () => void;
}

/** Fetch is deferred until the card scrolls into view so a large grid doesn't fire N requests at once. */
export function RepCard({ repUser, selectedConversationId, alertSettings, onSelectCall, onRemove }: IProps) {
	const [visible, setVisible] = React.useState(false);

	const repId = repUser.id;
	const repName = getDisplayName(repUser) || repId;

	const [filters, setFilters] = usePersistedRepCardFilters(repId);
	const [filtersOpen, setFiltersOpen] = usePersistedRepCardFiltersOpen(repId);

	const criteria = React.useMemo(() => buildRepCallCriteria(repId, filters), [repId, filters]);
	const {
		data,
		dataUpdatedAt,
		isSuccess,
		isLoading,
		isError,
		isFetching,
		refetch,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = usePhoneCallFilterQuery({
		criteria,
		enabled: visible,
		refetchInterval: visible ? POLL_INTERVAL_MS : false,
		// Alerts are the reason the card polls at all, so background polling follows the desktop toggle:
		// without it a hidden tab stops fetching and the notification it exists for never fires.
		refetchIntervalInBackground: RUBRIC_SCORE_CONTROLS_ENABLED && alertSettings.desktopEnabled,
	});

	const recap = useCoachingRecapMutation({
		onSuccess: () => toast.success(`Recap for ${repName} emailed to you.`),
		// A rep with nothing scored yet comes back as a 400 whose message says so — on a slow morning
		// that is the answer rather than a failure, so the server's copy has to reach the manager intact.
		onError: error => toast.error(apiErrorMessage(error, 'Couldn’t send the recap.')),
	});

	const calls = data?.calls ?? [];
	const visibleCalls = applyClientCallFilters(calls, filters);
	const totalCount = data?.totalCount ?? null;
	const count = hasClientCallFilters(filters) ? visibleCalls.length : totalCount;
	const countLabel = count != null ? `${count} call${count === 1 ? '' : 's'}` : '';

	useHighScoreCallAlerts({
		enabled: RUBRIC_SCORE_CONTROLS_ENABLED,
		calls: data?.calls,
		isSuccess,
		repName,
		threshold: alertSettings.threshold,
		desktopEnabled: alertSettings.desktopEnabled,
		baselineKey: JSON.stringify(criteria),
		onOpenCall: onSelectCall,
	});

	const renderRow = (view: PhoneCallView) => {
		const { phoneCall } = view;
		const aiConversationId = phoneCall.aiConversationId;
		const isSelected = Boolean(aiConversationId) && aiConversationId === selectedConversationId;
		const pitchAdherence = phoneCall.coachingAdherenceResult?.pitchAdherenceScore;
		const pitchAdherenceLabel = formatAdherencePercent(pitchAdherence);
		const suggestionAdherence = phoneCall.coachingAdherenceResult?.suggestionAdherenceScore;
		const suggestionAdherenceLabel = formatAdherencePercent(suggestionAdherence);
		const missedSound = phoneCall.coachingAdherenceResult?.missedSoundSuggestions ?? 0;
		const durationLabel = formatCallDuration(phoneCall.duration);
		const subject = getCallSubject(view);
		// Rows here are phone calls, so the call's own stamp is the right source. Absent on calls from
		// before the stamping until the backfill reaches this environment.
		const agentTemplate = formatAgentTemplate(phoneCall);

		return (
			<Button
				key={phoneCall.id}
				variant='ghost'
				disabled={!aiConversationId}
				onClick={() => aiConversationId && onSelectCall(aiConversationId)}
				styles={[styleSheet.row, ...(isSelected ? [styleSheet.rowSelected] : [])]}
				title={aiConversationId ? undefined : 'No conversation linked to this call'}
			>
				<div className={css(styleSheet.rowTopLine)}>
					<span className={css(styleSheet.rowSubject)}>{subject}</span>
					<div className={css(styleSheet.rowScores)}>
						<AdherencePill
							name='Pitch adherence'
							detail='whether the rep delivered the pitch their game plan recommended'
							score={pitchAdherence}
							value={`Pitch ${pitchAdherenceLabel ?? '—'}`}
						/>
						<AdherencePill
							name='Live suggestion adherence'
							detail='share of sound in-call advice the rep acted on'
							score={suggestionAdherence}
							value={`Live ${suggestionAdherenceLabel ?? '—'}`}
						/>
					</div>
				</div>
				<div className={css(styleSheet.rowMeta)}>
					<span>{formatCallTimestamp(phoneCall.creationDate)}</span>
					{agentTemplate ? (
						<span className={css(styleSheet.rowAgent)} title={agentTemplate.title ?? 'AI agent that ran this call'}>
							{agentTemplate.label}
						</span>
					) : null}
					{durationLabel ? (
						<span className={css(styleSheet.rowDuration)} title='Connected talk time'>
							{durationLabel}
						</span>
					) : null}
					{missedSound > 0 ? (
						<span
							className={css(styleSheet.rowMissed)}
							title={`${missedSound} sound coach suggestion(s) the rep did not act on`}
						>
							{missedSound} missed
						</span>
					) : null}
				</div>
			</Button>
		);
	};

	// With a client filter active, "no matches" is only ever about the pages fetched so far — say so
	// while Load more is still offered, or the two read as a contradiction.
	const emptyStateLabel = () => {
		if (!hasActiveRepCardFilters(filters)) {
			return 'No live-coaching calls.';
		}
		return hasNextPage ? 'No matches in the calls loaded so far.' : 'No calls match these filters.';
	};

	const renderBody = () => {
		if (!visible) {
			return null;
		}
		if (isLoading) {
			return (
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='small' />
				</div>
			);
		}
		if (isError) {
			return <div className={css(styleSheet.cardState)}>Couldn&rsquo;t load this rep&rsquo;s calls.</div>;
		}
		if (visibleCalls.length === 0) {
			return <div className={css(styleSheet.cardState)}>{emptyStateLabel()}</div>;
		}
		return visibleCalls.map(renderRow);
	};

	return (
		<div className={css(styleSheet.card)}>
			<div className={css(styleSheet.cardHeader)}>
				<div className={css(styleSheet.cardRepIdentity)}>
					<span className={css(styleSheet.cardRepName)} title={repName}>
						{repName}
					</span>
				</div>
				<div className={css(styleSheet.cardHeaderActions)}>
					{countLabel ? <span className={css(styleSheet.cardCount)}>{countLabel}</span> : null}
					<FreshnessBadge updatedAt={isSuccess ? dataUpdatedAt : null} />
					<Button
						variant='ghost'
						size='icon'
						styles={[styleSheet.cardHeaderIcon, ...(isFetching ? [styleSheet.spin] : [])]}
						onClick={() => refetch()}
						disabled={isFetching}
						aria-label={isFetching ? 'Refreshing calls' : 'Refresh calls'}
						title={isFetching ? 'Refreshing…' : 'Refresh'}
					>
						<RefreshIcon />
					</Button>
					<Button
						variant='ghost'
						size='icon'
						styles={[styleSheet.cardHeaderIcon]}
						onClick={() => recap.mutate({ repUserId: repId })}
						disabled={recap.isPending}
						aria-label={recap.isPending ? `Sending recap for ${repName}` : `Email me a recap for ${repName}`}
						title={recap.isPending ? 'Sending…' : 'Email me today’s recap'}
					>
						<RecapIcon />
					</Button>
					{onRemove ? (
						<Button
							variant='ghost'
							size='icon'
							styles={[styleSheet.cardHeaderIcon]}
							onClick={onRemove}
							aria-label={`Remove ${repName}`}
							title='Remove rep'
						>
							×
						</Button>
					) : null}
				</div>
			</div>
			<RepCardFilterBar
				filters={filters}
				onChange={setFilters}
				open={filtersOpen}
				onOpenChange={setFiltersOpen}
				repName={repName}
			/>
			<InViewInterceptor
				className={css(styleSheet.cardBody, !visible ? styleSheet.cardPlaceholder : null)}
				onIsIntersectingChange={isIntersecting => {
					if (isIntersecting) {
						setVisible(true);
					}
				}}
			>
				{renderBody()}
			</InViewInterceptor>
			{visible && calls.length > 0 && hasNextPage ? (
				<div className={css(styleSheet.cardFooter)}>
					<Button
						variant='secondary'
						size='sm'
						onClick={() => fetchNextPage()}
						disabled={isFetchingNextPage}
						styles={[styleSheet.loadMoreButton]}
					>
						{isFetchingNextPage ? 'Loading…' : 'Load more'}
					</Button>
				</div>
			) : null}
		</div>
	);
}
